import React, { useEffect, useState } from 'react';
import { Avatar, Wordmark } from '../../atoms';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { personalInfo } from '../../../../config/personalInfo';
import useFirebaseHeroHooks from './heroHooks';

const Hero = () => {
    const { language } = useLanguage();
    const [isVisible, setIsVisible] = useState(false);
    const [roleIndex, setRoleIndex] = useState(0);

    const {
        fullNameData: { data: fullNameData },
        textsData: { data: textsData },
        descriptionData: { data: descriptionData },
        greetingData: { data: greetingData },
        contactButtonData: { data: contactButtonData },
        projectsButtonData: { data: projectsButtonData },
        socialLinksData: { data: socialLinksData },
    } = useFirebaseHeroHooks(language);

    const roles = (Array.isArray(textsData) ? textsData : [])
        .map((role) => String(role ?? ''))
        .filter(Boolean);
    const currentRole = roles[roleIndex] || personalInfo.title;

    useEffect(() => {
        setIsVisible(true);
    }, []);

    useEffect(() => {
        if (roles.length < 2) return undefined;
        const id = window.setInterval(() => {
            setRoleIndex((prev) => (prev + 1) % roles.length);
        }, 3200);
        return () => window.clearInterval(id);
    }, [roles.length]);

    const scrollToContact = () => {
        document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
    };

    const scrollToProjects = () => {
        document.querySelector('#projects')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section
            id="home"
            className="relative min-h-screen overflow-hidden bg-ink grain"
            itemScope
            itemType="https://schema.org/Person"
        >
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <div className="absolute -left-24 top-1/4 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
                <div className="absolute -right-16 bottom-1/4 h-64 w-64 rounded-full bg-highlight/10 blur-3xl" />
            </div>

            <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-4 py-28 sm:px-6 lg:px-8">
                <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-10">
                    <div
                        className={`space-y-8 transition-all duration-700 lg:col-span-7 ${
                            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                        }`}
                    >
                        <Wordmark size="hero" className="block leading-none" />

                        <div className="space-y-3">
                            <p className="font-display text-lg text-muted md:text-xl">
                                {greetingData}
                                {' '}
                                <span className="text-fg" itemProp="name">
                                    {fullNameData || personalInfo.fullName}
                                </span>
                            </p>
                            <h1
                                className="font-display text-3xl font-bold tracking-tight text-fg md:text-4xl"
                                itemProp="jobTitle"
                            >
                                <span key={currentRole} className="inline-block animate-fade-up text-accent-soft">
                                    {currentRole}
                                </span>
                            </h1>
                        </div>

                        <p
                            className="max-w-xl text-lg leading-relaxed text-muted"
                            itemProp="description"
                        >
                            {descriptionData || personalInfo.description}
                        </p>

                        <div className="flex flex-col gap-3 sm:flex-row">
                            <button
                                type="button"
                                onClick={scrollToContact}
                                className="bg-accent px-7 py-3.5 font-semibold text-fg transition-colors duration-300 hover:bg-accent-soft"
                            >
                                {contactButtonData || 'Contact'}
                            </button>
                            <button
                                type="button"
                                onClick={scrollToProjects}
                                className="border border-highlight/70 px-7 py-3.5 font-semibold text-fg transition-colors duration-300 hover:border-highlight hover:text-highlight"
                            >
                                {projectsButtonData || 'Projects'}
                            </button>
                        </div>

                        <nav className="flex gap-3" aria-label="Social media links">
                            {socialLinksData?.github && (
                                <a
                                    href={socialLinksData.github}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="border border-white/10 bg-surface p-3 text-muted transition-colors hover:border-accent hover:text-accent"
                                    aria-label="Visit GitHub profile"
                                    itemProp="sameAs"
                                >
                                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                                    </svg>
                                </a>
                            )}
                            {socialLinksData?.linkedin && (
                                <a
                                    href={socialLinksData.linkedin}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="border border-white/10 bg-surface p-3 text-muted transition-colors hover:border-accent hover:text-accent"
                                    aria-label="Visit LinkedIn profile"
                                    itemProp="sameAs"
                                >
                                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                    </svg>
                                </a>
                            )}
                            {socialLinksData?.email && (
                                <a
                                    href={socialLinksData.email}
                                    className="border border-white/10 bg-surface p-3 text-muted transition-colors hover:border-accent hover:text-accent"
                                    aria-label="Send email"
                                    itemProp="email"
                                >
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </a>
                            )}
                        </nav>
                    </div>

                    <div
                        className={`flex justify-center transition-all duration-700 delay-150 lg:col-span-5 lg:justify-end ${
                            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                        }`}
                    >
                        <figure
                            className="hero-portrait"
                            style={{ width: '16rem', height: '20rem', flexShrink: 0 }}
                        >
                            <div
                                className="absolute -inset-10 rounded-full bg-accent/15 blur-3xl"
                                aria-hidden="true"
                            />

                            <div
                                className="absolute inset-3 translate-x-3 translate-y-4 rotate-[8deg] bg-highlight/35"
                                aria-hidden="true"
                                style={{ backgroundColor: 'rgba(197, 160, 33, 0.35)' }}
                            />
                            <div
                                className="absolute inset-2 -translate-x-2 translate-y-1 -rotate-[5deg] bg-accent/45"
                                aria-hidden="true"
                                style={{ backgroundColor: 'rgba(46, 139, 87, 0.45)' }}
                            />

                            <div
                                className="hero-portrait-float absolute inset-0 overflow-hidden bg-surface shadow-[0_28px_50px_-18px_rgba(0,0,0,0.85)]"
                                style={{
                                    clipPath: 'polygon(0 0, 100% 0, 100% 78%, 78% 100%, 0 100%)',
                                    backgroundColor: '#161616',
                                }}
                            >
                                <Avatar
                                    classnames="h-full w-full object-cover object-[center_12%] contrast-110 transition-transform duration-700 hover:scale-[1.03]"
                                    url={personalInfo.avatarUrl}
                                    alt={`n33no — ${fullNameData || personalInfo.fullName}`}
                                />
                                <div
                                    className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-ink/40 via-transparent to-accent/10"
                                    aria-hidden="true"
                                />
                            </div>

                            <div
                                className="absolute bottom-0 right-0 flex h-[22%] w-[22%] items-end justify-end bg-accent"
                                aria-hidden="true"
                            >
                                <span className="pb-1.5 pr-1.5 font-display text-[11px] font-extrabold tracking-tighter text-fg sm:text-xs">
                                    n3
                                </span>
                            </div>
                        </figure>
                    </div>
                </div>
            </div>
        </section>
    );
};

export { Hero };
